import { defineStore } from "pinia";
import { ref, computed } from "vue";
import { $api } from "@/services/api1";
import useFileUtilities from "@/use/fileUtilities";
import { useToast } from "@/use/useToast";
import type { ReportItem, ReportTab, RecentlyViewedReport, ReportExportFormat } from "@/types/reports";

const { triggerBlobDownload } = useFileUtilities();
const { show: showToast, dismiss: dismissToast } = useToast();

const RECENTLY_VIEWED_KEY = "cache.RecentlyViewedReports";
const RECENTLY_VIEWED_CAP = 8;

export const useReportsStore = defineStore("reports", () => {
  const tabs = ref<ReportTab[]>([]);
  const activeTabId = ref<number | null>(null);
  const isLoading = ref<boolean>(false);
  const error = ref<string | null>(null);
  const favoriteIds = ref<Set<number>>(new Set());
  const recentlyViewed = ref<RecentlyViewedReport[]>([]);
  const downloadingId = ref<number | null>(null);

  const activeTab = computed<ReportTab | null>(
    () => tabs.value.find((t) => t.id === activeTabId.value) ?? null
  );

  const favoriteReports = computed<ReportItem[]>(() => {
    const results: ReportItem[] = [];
    for (const tab of tabs.value) {
      for (const folder of tab.folders) {
        for (const report of folder.reports) {
          if (favoriteIds.value.has(report.id)) results.push(report);
        }
      }
      for (const report of tab.reports) {
        if (favoriteIds.value.has(report.id)) results.push(report);
      }
    }
    return results.sort((a, b) => a.displayName.localeCompare(b.displayName));
  });

  async function loadDashboard(): Promise<void> {
    isLoading.value = true;
    error.value = null;

    try {
      const data = await $api.reports.getDashboard();

      if (Array.isArray(data)) {
        tabs.value = data;
        activeTabId.value = data.length > 0 ? data[0].id : null;

        // Seed favoriteIds from the isFavorite flags returned by the dashboard
        favoriteIds.value = new Set(
          data
            .flatMap((t) => [
              ...t.folders.flatMap((f) => f.reports),
              ...t.reports,
            ])
            .filter((r) => r.isFavorite)
            .map((r) => r.id)
        );
      } else {
        failDashboardLoad();
      }
    } catch {
      failDashboardLoad();
    } finally {
      isLoading.value = false;
    }
  }

  function failDashboardLoad(): void {
    tabs.value = [];
    activeTabId.value = null;
    error.value = "Failed to load reports.";
    showToast(
      "We couldn't load the Data Warehouse reports. Please try again later.",
      "error",
      6000
    );
  }

  async function toggleFavorite(report: ReportItem): Promise<void> {
    const wasFavorite = favoriteIds.value.has(report.id);

    // Optimistic update
    if (wasFavorite) {
      favoriteIds.value.delete(report.id);
    } else {
      favoriteIds.value.add(report.id);
    }
    // Trigger reactivity on the Set
    favoriteIds.value = new Set(favoriteIds.value);

    try {
      if (wasFavorite) {
        await $api.reports.removeFavorite(report.id);
      } else {
        await $api.reports.addFavorite(report.id);
      }
    } catch {
      // Revert on failure
      if (wasFavorite) {
        favoriteIds.value.add(report.id);
      } else {
        favoriteIds.value.delete(report.id);
      }
      favoriteIds.value = new Set(favoriteIds.value);
    }
  }

  function logRecentlyViewed(report: ReportItem): void {
    const entry: RecentlyViewedReport = {
      id: report.id,
      displayName: report.displayName,
      timestamp: Date.now(),
    };

    // Dedupe by id, prepend, cap
    const updated = [
      entry,
      ...recentlyViewed.value.filter((r) => r.id !== report.id),
    ].slice(0, RECENTLY_VIEWED_CAP);

    recentlyViewed.value = updated;
    localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(updated));
  }

  function loadRecentlyViewed(): void {
    try {
      const raw = localStorage.getItem(RECENTLY_VIEWED_KEY);
      recentlyViewed.value = raw ? JSON.parse(raw) : [];
    } catch {
      recentlyViewed.value = [];
    }
  }

  function setActiveTab(tabId: number): void {
    activeTabId.value = tabId;
  }

  async function downloadReport(report: ReportItem, format: ReportExportFormat): Promise<boolean> {
    downloadingId.value = report.id;
    const toastId = showToast(`Preparing "${report.displayName}" for download…`, "info", 0);
    try {
      const { blob, filename } = await $api.reports.downloadReport(report.id, format);
      triggerBlobDownload(blob, filename);
      dismissToast(toastId);
      showToast(`"${report.displayName}" downloaded.`, "success", 4000);
      return true;
    } catch {
      dismissToast(toastId);
      showToast(`Failed to download "${report.displayName}".`, "error", 5000);
      return false;
    } finally {
      downloadingId.value = null;
    }
  }

  return {
    tabs,
    activeTabId,
    isLoading,
    error,
    favoriteIds,
    recentlyViewed,
    downloadingId,
    activeTab,
    favoriteReports,
    loadDashboard,
    toggleFavorite,
    logRecentlyViewed,
    loadRecentlyViewed,
    setActiveTab,
    downloadReport,
  };
});
